import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { post, patch, toastError } from "@/lib/api";
import { label } from "@/lib/labels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

/**
 * Status dropdown that POSTs {status, note} to `${endpoint}` and confirms with an optional note.
 */
export const StatusChanger = ({ endpoint, status, statuses, entity = "status", onChanged, disabled, testId = "status-changer", confirm = true, className, requireNoteFor = [], method = "post" }) => {
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  const m = useMutation({
    mutationFn: (body) => (method === "patch" ? patch(endpoint, body) : post(endpoint, body)),
    onSuccess: () => { toast.success(`Status endret til ${label(pending)}`); setPending(null); setNote(""); onChanged?.(); },
    onError: (e) => toastError(e),
  });
  const change = (v) => {
    if (v === status) return;
    if (confirm) setPending(v);
    else { setPending(v); m.mutate({ status: v }); }
  };
  return (
    <>
      <Select value={status} onValueChange={change} disabled={disabled || m.isPending}>
        <SelectTrigger data-testid={testId} className={className}><SelectValue /></SelectTrigger>
        <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} data-testid={`${testId}-option-${s}`}>{label(s)}</SelectItem>)}</SelectContent>
      </Select>
      {confirm && (
        <AlertDialog open={!!pending && !m.isPending} onOpenChange={(o) => !o && setPending(null)}>
          <AlertDialogContent data-testid={`${testId}-confirm`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Endre status til «{label(pending)}»?</AlertDialogTitle>
              <AlertDialogDescription>Endringen loggføres i historikken. Legg gjerne ved en kommentar.</AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Kommentar (valgfritt)" rows={2} data-testid={`${testId}-note-input`} />
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction disabled={requireNoteFor.includes(pending) && !note.trim()} onClick={(e) => { e.preventDefault(); m.mutate({ status: pending, note: note || null }); }} data-testid={`${testId}-confirm-button`}>Bekreft</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
};

export const HistoryList = ({ items, field = "status", entity }) => {
  if (!items?.length) return <div className="text-xs text-muted-foreground">Ingen historikk</div>;
  return (
    <ol className="space-y-1.5 text-sm" data-testid="status-history">
      {items.slice().reverse().map((h, i) => (
        <li key={i} className="flex flex-wrap items-center gap-2">
          <span className="inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset bg-muted">{label(h[field])}</span>
          <span className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString("nb-NO")} · {h.by}{h.note ? ` · ${h.note}` : ""}</span>
        </li>
      ))}
    </ol>
  );
};
